/**
 * New Template, from Firm Knowledge > Intake Templates: start blank, from a
 * CaseBlink starter (a copy the firm can change), from an intake already
 * made for a client (its questions, note, video, link and reminders), or ask
 * Casey to draft one. Each opens the new template in its editor.
 */
import {
  ChevronDown,
  FileText,
  LayoutTemplate,
  Loader2,
  Plus,
  Search,
  Send,
  Sparkles
} from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type { OutgoingIntake } from "@/api/types/outgoingIntake";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { NavRow } from "@/components/ui/nav-row";
import { Spinner } from "@/components/ui/spinner";
import { formatMdY } from "@/features/clientProfile/lib/format";
import { useClientSearch } from "@/features/newCase/hooks/useClientSearch";
import type { SelectedClient } from "@/features/newCase/types";
import { clientInitials } from "@/features/newCase/utils";
import { ASSISTANT_NAME } from "@/features/requestsIde/model";

import type { IntakeTemplatesState } from "../hooks/useIntakeTemplates";
import { asIso, duplicateName, groupTemplates } from "../intakeTemplates";

type Props = {
  templates: IntakeTemplatesState;
  onCreated: (templateId: string) => void;
  /** Ask Casey to draft a template. */
  onAskCasey?: () => void;
};

type Picker = "starter" | "intake" | null;

const sentOn = (intake: OutgoingIntake) => {
  const sent = asIso(intake.sent_at);
  return sent ? `Sent ${formatMdY(sent)}` : "Draft, not sent";
};

const SearchBox: FC<{
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  label: string;
  testId: string;
}> = ({ value, onChange, placeholder, label, testId }) => (
  <div className="relative">
    <Search
      className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-3"
      aria-hidden
    />
    <Input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className="pl-8"
      data-testid={testId}
    />
  </div>
);

const StarterPicker: FC<{
  templates: IntakeTemplatesState;
  busy: boolean;
  onPick: (starterId: string) => void;
}> = ({ templates, busy, onPick }) => {
  const [query, setQuery] = useState("");
  const { starters } = useMemo(
    () => groupTemplates([], templates.catalog, query),
    [templates.catalog, query]
  );
  return (
    <div className="flex flex-col gap-2">
      <SearchBox
        value={query}
        onChange={setQuery}
        placeholder="Search starters…"
        label="Search Starters"
        testId="new-template-starter-search"
      />
      <div className="flex max-h-72 flex-col overflow-y-auto" role="listbox">
        {starters.length === 0 ? (
          <p className="px-2 py-2 text-sm text-fg-3">No starter matches.</p>
        ) : (
          starters.map((s) => (
            <NavRow
              key={s.id}
              role="option"
              aria-selected={false}
              disabled={busy}
              onClick={() => onPick(s.id)}
              className="h-auto py-1.5"
              data-testid="new-template-starter"
            >
              <span className="flex min-w-0 items-center gap-2.5">
                {s.form ? (
                  <FileText className="size-3.5 shrink-0 text-fg-3" />
                ) : (
                  <LayoutTemplate className="size-3.5 shrink-0 text-fg-3" />
                )}
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-fg">{s.name}</span>
                  <span className="truncate text-sm text-fg-3">
                    {[s.visa, s.size].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </span>
            </NavRow>
          ))
        )}
      </div>
    </div>
  );
};

const IntakePicker: FC<{
  busy: boolean;
  onPick: (client: SelectedClient, intake: OutgoingIntake) => void;
}> = ({ busy, onPick }) => {
  const [query, setQuery] = useState("");
  const [client, setClient] = useState<SelectedClient | null>(null);
  const [intakes, setIntakes] = useState<OutgoingIntake[] | null>(null);
  const search = useClientSearch(client ? "" : query);

  useEffect(() => {
    if (!client) return undefined;
    let cancelled = false;
    setIntakes(null);
    OutgoingIntakesAPI.listForForeignNational(client.id)
      .then((list) => {
        if (cancelled) return;
        setIntakes(
          list.filter((i) => (i.schema ?? []).length > 0 && !i.cancelled_at)
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setIntakes([]);
        toast.error(
          e instanceof Error ? e.message : "Couldn't load their intakes"
        );
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  if (!client)
    return (
      <div className="flex flex-col gap-2">
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Search clients…"
          label="Search Clients"
          testId="new-template-client-search"
        />
        <div className="flex max-h-72 flex-col overflow-y-auto" role="listbox">
          {search.isLoading ? (
            <div className="flex items-center gap-2 px-2 py-2 text-sm text-fg-3">
              <Spinner className="size-3.5" /> Finding clients…
            </div>
          ) : search.clients.length === 0 ? (
            <p className="px-2 py-2 text-sm text-fg-3">
              {search.error ?? "No client matches."}
            </p>
          ) : (
            search.clients.slice(0, 8).map((c) => (
              <NavRow
                key={c.id}
                role="option"
                aria-selected={false}
                onClick={() => setClient(c)}
                className="h-10"
                data-testid="new-template-client"
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <Avatar size="sm">
                    <AvatarFallback>{clientInitials(c)}</AvatarFallback>
                  </Avatar>
                  <span className="truncate text-fg">{c.name}</span>
                </span>
              </NavRow>
            ))
          )}
        </div>
      </div>
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm text-fg-2">
          {client.name}&apos;s intakes
        </span>
        <Button variant="ghost" size="xs" onClick={() => setClient(null)}>
          Change Client
        </Button>
      </div>
      <div className="flex max-h-72 flex-col overflow-y-auto" role="listbox">
        {intakes === null ? (
          <div className="flex justify-center py-4">
            <Spinner className="size-4" />
          </div>
        ) : intakes.length === 0 ? (
          <p className="px-2 py-2 text-sm text-fg-3">
            No intakes with questions yet.
          </p>
        ) : (
          intakes.map((i) => (
            <NavRow
              key={i.outgoing_intake_id}
              role="option"
              aria-selected={false}
              disabled={busy}
              onClick={() => onPick(client, i)}
              className="h-auto py-1.5"
              data-testid="new-template-intake"
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <Send className="size-3.5 shrink-0 text-fg-3" />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-fg">
                    {i.title || "Untitled intake"}
                  </span>
                  <span className="truncate text-sm text-fg-3">
                    {sentOn(i)}
                  </span>
                </span>
              </span>
            </NavRow>
          ))
        )}
      </div>
    </div>
  );
};

export const NewTemplateMenu: FC<Props> = ({
  templates,
  onCreated,
  onAskCasey
}) => {
  const [picker, setPicker] = useState<Picker>(null);
  const [busy, setBusy] = useState(false);

  const run = async (make: () => Promise<string | null>) => {
    setBusy(true);
    try {
      const id = await make();
      if (id) {
        setPicker(null);
        onCreated(id);
      }
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Couldn't create a template"
      );
    } finally {
      setBusy(false);
    }
  };

  const blank = () =>
    run(async () => {
      const created = await OutgoingIntakesAPI.createSavedTemplate({
        name: "Untitled Intake Template",
        schema: []
      });
      templates.applySaved(created);
      return created.template_id;
    });

  const fromStarter = (starterId: string) =>
    run(async () => {
      const starter = templates.catalogById(starterId);
      const schema = await templates.loadStarterSchema(starterId);
      const created = await OutgoingIntakesAPI.createSavedTemplate({
        name: duplicateName({
          name: starter?.label ?? "Starter",
          kind: "starter"
        }),
        schema,
        visa: starter?.visa ?? null,
        description: starter?.description ?? null
      });
      templates.applySaved(created);
      toast.success(`Made “${created.name}” from the starter`);
      return created.template_id;
    });

  const fromIntake = (client: SelectedClient, intake: OutgoingIntake) =>
    run(async () => {
      const { template, already_saved: already } =
        await OutgoingIntakesAPI.saveIntakeAsTemplate(
          client.id,
          intake.outgoing_intake_id
        );
      templates.applySaved(template);
      toast.success(
        already
          ? `That intake is already the template “${template.name}”`
          : `Made “${template.name}” from ${client.name}'s intake`
      );
      return template.template_id;
    });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            data-testid="new-template"
          >
            {busy ? <Loader2 className="animate-spin" /> : <Plus />}
            New Template
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuItem
            onSelect={() => void blank()}
            data-testid="new-template-blank"
          >
            <Plus /> Blank Template
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setPicker("starter")}
            data-testid="new-template-from-starter"
          >
            <LayoutTemplate /> From a CaseBlink Starter…
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setPicker("intake")}
            data-testid="new-template-from-intake"
          >
            <Send /> From a Sent Intake…
          </DropdownMenuItem>
          {onAskCasey ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={onAskCasey}
                data-testid="new-template-ask-casey"
              >
                <Sparkles /> Ask {ASSISTANT_NAME} to Draft…
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={picker !== null}
        onOpenChange={(open) => !open && setPicker(null)}
      >
        <DialogContent
          className="grid-cols-1 sm:max-w-md"
          data-testid="new-template-picker"
        >
          <DialogHeader>
            <DialogTitle>
              {picker === "intake"
                ? "New Template From an Intake"
                : "New Template From a Starter"}
            </DialogTitle>
            <DialogDescription>
              {picker === "intake"
                ? "Its questions, note, video, link and reminders become the template. The intake doesn't change."
                : "A copy your firm can change. The starter stays as CaseBlink keeps it."}
            </DialogDescription>
          </DialogHeader>
          {picker === "intake" ? (
            <IntakePicker
              busy={busy}
              onPick={(c, i) => void fromIntake(c, i)}
            />
          ) : picker === "starter" ? (
            <StarterPicker
              templates={templates}
              busy={busy}
              onPick={(id) => void fromStarter(id)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
